import React, { ComponentProps, useId, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormSyncErrors, touch } from 'redux-form';
import { Button, Text } from 'shared-components';
import { RootState } from '@store/reducers.js';

type ButtonProps = ComponentProps<typeof Button>;

interface BCMShortSubmitButtonProps extends Omit<ButtonProps, 'variant' | 'type' | 'children'> {
  children?: React.ReactNode;
  invalid?: boolean;
  saving?: boolean;
  /** redux-form whose registered fields are marked touched on an invalid attempt. */
  formName?: string;
}

// Errors render below their field, so centring the first message shows the
// field too. Falls back to the first control flagged invalid.
const revealFirstError = (root: ParentNode | null) => {
  const target = (root ?? document).querySelector<HTMLElement>(
    '.error-text, [data-invalid="true"], [aria-invalid="true"]'
  );
  target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
};

// Canonical "Continue" CTA used at the bottom of every BCM-Short step.
// Matches Volt's wizard button styling exactly. When `saving` is true,
// the shared Button renders its built-in <LoadingDots /> animation in
// place of the children — visible loading state on the button itself,
// no full-page overlay needed for intermediate steps.
//
// The button stays enabled while the step is invalid (mobile UX audit
// 2026-10, F10). A disabled submit gave no feedback at all: redux-form only
// shows a field's error once it is touched, so a user who tapped Continue
// with empty fields saw nothing happen. An invalid attempt now marks every
// field on the step as touched (each renders its own message), scrolls the
// first message into view and announces how many fields are left. Only
// `saving` disables the button.
export const BCMShortSubmitButton: React.FC<BCMShortSubmitButtonProps> = ({
  children,
  invalid,
  saving,
  className,
  formName = 'application',
  onClick,
  ...rest
}) => {
  const dispatch = useDispatch();
  const hintId = useId();
  const [attempted, setAttempted] = useState(false);
  const registeredFields = useSelector((state: RootState) => state.form?.[formName]?.registeredFields);
  const syncErrors = useSelector(
    (state: RootState) => getFormSyncErrors(formName)(state) as Record<string, unknown> | undefined
  );

  const showHint = attempted && !!invalid;
  const missing = showHint
    ? Object.keys(registeredFields ?? {}).filter((name) => Boolean(syncErrors?.[name])).length
    : 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!invalid || saving || event.defaultPrevented) return;
    event.preventDefault();
    setAttempted(true);
    dispatch(touch(formName, ...Object.keys(registeredFields ?? {})));
    const form = event.currentTarget.form;
    window.requestAnimationFrame(() => revealFirstError(form));
  };

  return (
    <div className="flex flex-col items-center gap-[10px] w-full">
      <Button
        variant="cta-form"
        type="submit"
        isLoading={saving}
        disabled={saving}
        data-invalid={invalid || undefined}
        aria-describedby={showHint ? hintId : undefined}
        {...rest}
        onClick={handleClick}
        className={`px-[50px] py-[12.5px] shadow-[0px_0px_15px_0px_rgba(13,13,13,0.05)] ${className ?? ''}`.trim()}
      >
        {children ?? 'Continue'}
      </Button>
      <div id={hintId} role="status" aria-live="polite" className="text-center">
        {showHint && (
          <Text variant="error" data-testid="continue-missing-fields">
            {missing > 0
              ? `${missing} required field${missing === 1 ? '' : 's'} left`
              : 'Please complete the fields above.'}
          </Text>
        )}
      </div>
    </div>
  );
};
